import { forwardRef, type ComponentPropsWithoutRef, type MouseEvent } from 'react'
import cn from 'clsx'
import { Link } from '@ds/components'
import type { TMenuItemView } from '../types'
import styles from './MenuLinkItem.module.css'

/**
 * The three row shapes audio-web ships: `list` is the narrow Listen/More column, `grid` a feature
 * cell with icon + description, `menu` a user-menu row.
 */
type TShape = 'list' | 'grid' | 'menu'

type TMenuLinkItemProps = {
    item: TMenuItemView
    newLabel: string
    shape?: TShape
} & Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'children' | 'rel'>

/**
 * The row only — the Radix wrapper is the caller's, because the two callers sit in different
 * primitives: site navigation needs `NavigationMenuLink`, the user menu needs `MenuItem`, and each
 * throws outside its own root.
 */
export const MenuLinkItem = forwardRef<HTMLAnchorElement, TMenuLinkItemProps>(
    ({ item, newLabel, shape = 'menu', onClick, ...rest }, ref) => {
        const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event)
            item.onClick?.()
        }

        return (
            <Link
                ref={ref}
                variant="primary"
                href={item.href}
                data-active={item.isActive || undefined}
                {...rest}
                className={cn(styles.dropdownItem, styles[`shape-${shape}`])}
                onClick={handleClick}
            >
                {item.Icon ? (
                    <span className={styles.dropdownIconSlot}>
                        <item.Icon className={styles.dropdownIcon} />
                    </span>
                ) : null}
                <span className={styles.dropdownText}>
                    <span className={styles.dropdownTitle}>{item.label}</span>
                    {item.description ? (
                        <span className={styles.dropdownDesc}>{item.description}</span>
                    ) : null}
                </span>
                {item.isNew ? <span className={styles.new}>{newLabel}</span> : null}
            </Link>
        )
    },
)
